import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  MiniMap,
  Panel,
  applyNodeChanges,
  applyEdgeChanges,
  ConnectionMode,
  SelectionMode,
  type NodeChange,
  type EdgeChange,
  useReactFlow,
  type ReactFlowProps,
} from "@xyflow/react";
import { Button } from "@trillion/ui/components/ui/button";
import { Input } from "@trillion/ui/components/ui/input";
import { Badge } from "@trillion/ui/components/ui/badge";
import { cn } from "@trillion/ui/lib/utils";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { ApiError } from "@trillion/ui/lib/api-client";
import { api } from "../lib/api";
import { useDiagramSync } from "../lib/use-diagram-sync";
import { ShareDialog } from "../components/share-dialog";
import { CommentsPanel } from "../components/comments-panel";
import { VersionHistoryPanel } from "../components/version-history-panel";
import { DiagramToolbar } from "../components/diagram-toolbar";
import { nodeTypes } from "../components/nodes";
import { edgeTypes } from "../components/edges";
import { ArrowMarkerDefs } from "../components/edges/arrow-markers";
import { canonicalDocument, decodeBoardSnapshot, decodeCheckpoint, downloadRecovery, recoveryCopy } from "../lib/board-document";
import { collaborationCheckpoint, compareRevision, higherCheckpoint, preserveCollaborationView, reconcileCheckpoint, retainRecovery, type RecoveryDocument } from "../lib/diagram-collaboration";
import { CollaborationStatus } from "../components/collaboration-status";
import { CollaboratorCursors } from "../components/collaborator-cursors";
import { useDiagramSave } from "../lib/use-diagram-save";
import { SaveRecoveryPanel } from "../components/save-recovery-panel";
import type { DiagramSaveResult, BoardSnapshot, DiagramSyncCheckpoint, DiagramRefresh, DiagramJoin } from "../types/board";
import type { BoardNodeData, OrganizationAction } from "../types/board";
import { createBoardNode, NODE_DEFAULT_SIZES, parseNodeDrop } from "../lib/node-creation";
import { CanvasEditabilityProvider, useCanvasMutationGate } from "../lib/canvas-editability";
import { getBoardPermissions } from "../lib/board-permissions";
import { beginHistoryPreview, exitHistoryPreview, type PreviewSession } from "../lib/history-preview";
import { useCanvasHistory } from "../lib/use-canvas-history";
import { allocateGroupId, expandGroupSelection, groupId, groupSelectionChanges } from "../lib/canvas-groups";
import { nodeOrganizationBox, organizationState, organize, translateGroupChanges, type OrganizationBox } from "../lib/canvas-organization";
import type { CanvasGraph } from "../lib/canvas-commands";
import { insertElements, removeElements } from "../lib/canvas-commands";
import { canReconnect, connectionEndpoints, createConnection, displayedConnectionEdges, isConnectionPoint,
  isValidConnection, reconnectConnection, type ReconnectOrigin } from "../lib/canvas-connections";
import { useCanvasClipboard } from "../lib/use-canvas-clipboard";
import { clipboardPlacement, selectionAnchor, type PasteCascade } from "../lib/canvas-clone";
import { canvasShortcut, isCanvasShortcutTarget } from "../lib/canvas-shortcuts";
import { canvasInteraction, navigationAvailability, navigationTargets, selectCanvas, MIN_CANVAS_ZOOM, MAX_CANVAS_ZOOM, type NavigationAction } from "../lib/canvas-navigation";
import { useCanvasNavigation } from "../lib/use-canvas-navigation";
import { CanvasNavigationControls } from "../components/canvas-navigation-controls";
import { DiagramRole } from "../types/enums";

interface DiagramDetail {
  diagram_id: string;
  title: string;
  snapshot: string | null;
  revision: string;
  modified_date: string;
}

/** A REST diagram detail: the diagram, the caller's role, whether the caller manages its share list and, for a manager,
 * the pending access-request count (null otherwise). */
interface DiagramDetailResponse {
  diagram: DiagramDetail;
  effective_diagram_role_id?: DiagramRole | null;
  can_manage_sharing?: boolean;
  pending_access_request_count?: number | null;
}

// Inner component wrapped by ReactFlowProvider so useReactFlow() works
function DiagramCanvas() {
  const { diagramId } = useParams<{ diagramId: string }>();
  const [diagram, setDiagram] = useState<DiagramDetail | null>(null);
  const [title, setTitle] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const terminal = useRef(false);
  const [recoveryCopies, setRecoveryCopies] = useState<RecoveryDocument[]>([]);
  const pendingCheckpoint = useRef<(DiagramSyncCheckpoint & { detail?: { diagram: DiagramDetail; effective_diagram_role_id?: DiagramRole | null } }) | null>(null);
  const degradeRef = useRef(() => {});
  const [loadAttempt, setLoadAttempt] = useState(0);
  const rawLoad = useRef<unknown>(null);
  const routeEpoch = useRef(0);
  const snapshotRoot = useRef<BoardSnapshot>(decodeBoardSnapshot(null));

  const [shortcutReference, setShortcutReference] = useState<"button" | "canvas" | null>(null);
  const [zoom, setZoom] = useState(1);
  const [shareOpen, setShareOpen] = useState(false);
  // Only a sharing manager sees Share. Set from every REST detail and every hub refresh that carries the flag.
  const [canManageSharing, setCanManageSharing] = useState(false);
  // Pending access requests, shown on Share. Set with the flag from every REST detail and hub refresh, and by the dialog.
  const [pendingRequests, setPendingRequests] = useState(0);
  // A 403 before any checkpoint loaded: the user never had this diagram, so the unavailable state offers Request access.
  // A mid-session revocation (a 403 after a load, or the hub's Invalidate) never sets it.
  const [noAccess, setNoAccess] = useState(false);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [effectiveRole, setEffectiveRole] = useState<DiagramRole | null>(null);
  const [preview, setPreview] = useState<PreviewSession | null>(null);
  const previewRef = useRef<PreviewSession | null>(null);
  const isPreviewing = preview !== null;
  const authorityRef = useRef({ loaded: false, role: null as DiagramRole | null, disposed: false });
  const previewRequestKey = useRef(0);
  const getPreviewRequestKey = useCallback(() => previewRequestKey.current, []);
  const invalidatePreviewRequests = useCallback(() => { previewRequestKey.current++; }, []);

  const history = useCanvasHistory();
  const { nodes, edges } = history.state.graph;
  const { reset: resetGraph, closeAuthority, current: historyRef, view: updateView } = history;
  const rfInstance = useReactFlow();
  const { setViewport } = rfInstance;
  const canvasRef = useRef<HTMLDivElement>(null);
  const pasteCascade = useRef<PasteCascade | null>(null);
  const cancelConnectionIntent = useRef<(() => void) | null>(null);
  const selectionBoxGeneration = useRef<number | null>(null);

  const workingDocument = useMemo(() => ({ title, snapshot: { ...snapshotRoot.current, nodes, edges } }), [title, nodes, edges]);
  const saveDocument = useCallback(async (capture: import("../types/board").DiagramCheckpoint, source: "manual" | "autosave") => {
    const result = await api<DiagramSaveResult>(`/api/diagrams/${diagramId}/snapshot`, {
      method: "PUT", body: JSON.stringify({ expected_revision: capture.revision,
        title: capture.document.title, snapshot: JSON.stringify(capture.document.snapshot), source }),
    });
    if (!result.success || result.diagram.diagram_id !== diagramId) throw new Error("Invalid save acknowledgement");
    if (result.collaboration === "degraded") degradeRef.current();
    return decodeCheckpoint(result.diagram);
  }, [diagramId]);
  const { checkpoint, submitted, operationRef, operation, saveState, failure, fail, conflict, invalidate, adopt, advanceRevision, begin, doSave: saveCaptured, dirty } = useDiagramSave(workingDocument, saveDocument);
  const workingRef = useRef(workingDocument); workingRef.current = workingDocument;
  const currentDocument = useCallback(() => ({ title: workingRef.current.title, snapshot: { ...snapshotRoot.current, ...historyRef.current.graph } }), [historyRef]);
  const needsFitViewRef = useRef(false);
  const permissions = getBoardPermissions(effectiveRole, isPreviewing);
  const canEditDocument = !unavailable && !isLoading && !error && permissions.canEditDocument && operation !== "restore" && operation !== "reload";
  const canRestore = !unavailable && !isLoading && !error && permissions.canRestore;
  const showSharing = canManageSharing && !unavailable;
  const isCurrentlyEditable = useCallback(() => {
    const authority = authorityRef.current;
    return !terminal.current && authority.loaded && !authority.disposed && previewRef.current === null &&
      operationRef.current !== "restore" && operationRef.current !== "reload" && getBoardPermissions(authority.role).canEditDocument;
  }, []);
  const editability = useCanvasMutationGate({
    canEditDocument, isDocumentEditable: isCurrentlyEditable, history,
    onReplayRejected: () => toast.info("Undo history cleared because the diagram changed outside the editor."),
    disabledReason: isPreviewing ? "Formatting is unavailable during preview."
      : operation === "restore" ? "A version is being restored." : "This diagram is read-only.",
  });
  const { isDocumentEditable, runDocumentMutation, execute, begin: beginEdit, finish: finishEdit, undo, redo, generation } = editability;
  const navigation = useCanvasNavigation({ canvas: canvasRef,
    resetKey: `${diagramId}:${generation}:${isPreviewing}:${operation === "reload" || operation === "restore"}`,
    onCancel: () => {
      cancelConnectionIntent.current?.();
      selectionBoxGeneration.current = null;
      const session = historyRef.current.session;
      if (session && ["drag", "resize"].includes(session.kind)) finishEdit(session.token);
    },
  });
  const interaction = canvasInteraction(navigation.mode, navigation.temporaryPan, canEditDocument);
  const returnCanvasFocus = useCallback(() => canvasRef.current?.focus({ preventScroll: true }), []);
  const selectVisible = useCallback((selected: boolean) => {
    const session = previewRef.current;
    if (session) {
      const next = { ...session, graph: selectCanvas(session.graph, selected) };
      previewRef.current = next; setPreview(next);
    } else updateView(graph => selectCanvas(graph, selected));
  }, [updateView]);
  const measuredGraph = useCallback(() => {
    const graph = previewRef.current?.graph ?? historyRef.current.graph;
    return { ...graph, nodes: graph.nodes.map(node => ({ ...node, measured: rfInstance.getNode?.(node.id)?.measured ?? node.measured })) };
  }, [historyRef, rfInstance]);
  const onNavigate = useCallback((action: NavigationAction): boolean => {
    const graph = measuredGraph();
    if (!navigationAvailability(graph, rfInstance.getViewport().zoom)[action].enabled) return false;
    if (action === "zoomIn") void rfInstance.zoomIn();
    else if (action === "zoomOut") void rfInstance.zoomOut();
    else if (action === "actualSize") void rfInstance.zoomTo(1);
    else void rfInstance.fitView({ nodes: navigationTargets(graph, action === "fitSelection"), padding: 0.2,
      minZoom: MIN_CANVAS_ZOOM, maxZoom: MAX_CANVAS_ZOOM });
    return true;
  }, [measuredGraph, rfInstance]);
  const clipboard = useCanvasClipboard({
    route: diagramId ?? "", generation,
    canRead: !isLoading && !error && operation !== "restore" && operation !== "reload",
    canEdit: canEditDocument,
    hasSelection: (preview?.graph.nodes ?? nodes).some(node => node.selected),
    getGeneration: () => historyRef.current.epoch,
    isReadable: () => authorityRef.current.loaded && !authorityRef.current.disposed &&
      operationRef.current !== "restore" && operationRef.current !== "reload",
    isEditable: isDocumentEditable,
    // Read canonical content with its visible selection, before read-only rendering overlays.
    getVisibleGraph: () => previewRef.current?.graph ?? historyRef.current.graph,
    getWorkingGraph: () => historyRef.current.graph,
    execute,
    placement: (payload, action) => {
      const anchor = selectionAnchor(payload);
      const placement = clipboardPlacement({ bounds: canvasRef.current?.getBoundingClientRect() ?? null,
        viewport: rfInstance.getViewport(), payloadKey: JSON.stringify(payload),
        generation: `${diagramId}:${historyRef.current.epoch}`, previous: pasteCascade.current,
        ...(action === "duplicate" ? { duplicateAnchor: anchor } : {}),
      });
      if (!placement) return null;
      const point = rfInstance.screenToFlowPosition(placement.screen, { snapToGrid: false });
      pasteCascade.current = action === "paste" ? placement.cascade : null;
      return { x: point.x - anchor.x, y: point.y - anchor.y };
    },
  });
  const resolveBoxes = useCallback((graphNodes: typeof nodes) => new Map(graphNodes.flatMap(node => {
    const box = nodeOrganizationBox(node, rfInstance.getNode?.(node.id)?.measured ?? node.measured);
    return box ? [[node.id, box] as [string, OrganizationBox]] : [];
  })), [rfInstance]);
  const visibleGraph = preview?.graph ?? { nodes, edges };
  const visibleBoxes = resolveBoxes(visibleGraph.nodes);
  const organization = organizationState(visibleGraph, visibleBoxes,
    !canEditDocument ? isPreviewing ? "Organization is unavailable during preview." : "This diagram is read-only." : undefined);
  const onOrganize = useCallback((action: OrganizationAction) => {
    if (!isDocumentEditable() || historyRef.current.epoch !== generation) return;
    const graph = historyRef.current.graph, boxes = resolveBoxes(graph.nodes);
    if (!organizationState(graph, boxes).actions[action].enabled) return;
    const id = action === "group" ? allocateGroupId(graph) : undefined;
    if (id === null) { toast.error("Could not allocate a fresh group identity. Try again."); return; }
    execute(organize(action, boxes, id));
  }, [isDocumentEditable, historyRef, generation, resolveBoxes, execute]);
  // Load and replay can preserve only part of an old selection. Close membership
  // as personal view state before clipboard or organization reads it.
  useEffect(() => {
    const close = (graph: CanvasGraph): CanvasGraph => {
      const next = groupSelectionChanges(graph.nodes, []);
      return next.some((node, index) => !!node.selected !== !!graph.nodes[index].selected) ? { ...graph, nodes: next } : graph;
    };
    const session = previewRef.current;
    if (session) {
      const graph = close(session.graph);
      if (graph !== session.graph) { const next = { ...session, graph }; previewRef.current = next; setPreview(next); }
    } else updateView(close);
  }, [nodes, preview, updateView]);
  const displayedNodes = useMemo(() => {
    const graphNodes = preview?.graph.nodes ?? nodes, boxes = resolveBoxes(graphNodes);
    const restricted = new Set(graphNodes.filter(node => groupId(node) !== undefined && !boxes.has(node.id)).map(groupId));
    return groupSelectionChanges(graphNodes, []).map(node => interaction.panning ? {
      ...node, draggable: false, connectable: false, selectable: false, ...(!canEditDocument ? { deletable: false } : {}),
    } : !canEditDocument ? {
      ...node, draggable: false, connectable: false, deletable: false,
    } : restricted.has(groupId(node)) ? { ...node, draggable: false } : node);
  }, [nodes, preview, canEditDocument, interaction.panning, resolveBoxes]);
  const displayedEdges = useMemo(() => {
    const graphEdges = displayedConnectionEdges(preview?.graph ?? { nodes, edges });
    if (interaction.panning) return graphEdges.map(edge => ({ ...edge, selectable: false, reconnectable: false, ...(!canEditDocument ? { deletable: false } : {}) }));
    return canEditDocument ? graphEdges : graphEdges.map((edge) => ({
      ...edge, deletable: false, reconnectable: false,
    }));
  }, [nodes, edges, preview, canEditDocument, interaction.panning]);

  // Capture the gate generation in framework callbacks too: a retained callback
  // from the previous accepted graph cannot start a session on its replacement.
  const currentGeneration = useCallback(() => historyRef.current.epoch === generation &&
    navigation.isGestureCurrent(navigation.gestureGeneration), [historyRef, generation, navigation.isGestureCurrent, navigation.gestureGeneration]);
  const startDrag = useCallback(() => {
    if (!currentGeneration() || !isDocumentEditable() || navigation.isPanning()) return;
    if (historyRef.current.session?.kind !== "drag") beginEdit("drag");
  }, [beginEdit, currentGeneration, historyRef, isDocumentEditable, navigation.isPanning]);
  const endDrag = useCallback(() => {
    if (!currentGeneration() || !isDocumentEditable()) return;
    if (historyRef.current.session?.kind === "drag") finishEdit(historyRef.current.session.token);
  }, [finishEdit, historyRef, currentGeneration, isDocumentEditable]);
  const onNodesChange = useCallback((changes: NodeChange[]) => {
    if (!currentGeneration()) return;
    if (navigation.isPanning()) changes = changes.filter(change => change.type === "dimensions" && !change.setAttributes && change.resizing === undefined);
    const viewOnly = changes.filter(change => change.type === "select" ||
      (change.type === "dimensions" && !change.setAttributes && change.resizing === undefined));
    const selections = changes.filter((change): change is Extract<NodeChange, { type: "select" }> => change.type === "select");
    const applyView = (changes: NodeChange[], graphNodes: typeof nodes) =>
      groupSelectionChanges(applyNodeChanges(changes, graphNodes), selections, selectionBoxGeneration.current === generation);
    const previewSession = previewRef.current;
    if (previewSession) {
      const next = { ...previewSession, graph: { ...previewSession.graph,
        nodes: applyView(viewOnly, previewSession.graph.nodes) } };
      previewRef.current = next; setPreview(next); return;
    }
    if (!isDocumentEditable()) {
      updateView(graph => ({ ...graph, nodes: applyView(viewOnly, graph.nodes) })); return;
    }
    const authored = changes.some(change => change.type !== "select" &&
      !(change.type === "dimensions" && !change.setAttributes && change.resizing === undefined));
    if (!authored) {
      updateView(graph => ({ ...graph, nodes: applyView(changes, graph.nodes) })); return;
    }
    const resizing = changes.some(change => change.type === "dimensions" && change.resizing === true);
    const dragging = changes.some(change => change.type === "position" && change.dragging === true);
    const kind = resizing ? "resize" : dragging ? "drag" : null;
    if (kind && historyRef.current.session?.kind !== kind) beginEdit(kind);
    const session = historyRef.current.session;
    const token = session && ["drag", "resize"].includes(session.kind) ? session.token : undefined;
    execute(graph => {
      const individualResize = session?.kind === "resize" || changes.some(change => change.type === "dimensions" && change.resizing !== undefined);
      const positions = changes.flatMap(change => change.type === "position" && change.position ? [{ id: change.id, position: change.position }] : []);
      const moved = individualResize ? graph.nodes : translateGroupChanges(graph, positions, resolveBoxes(graph.nodes));
      const remaining = individualResize ? changes : changes.map(change =>
        change.type === "position" ? { ...change, position: undefined } : change);
      return { ...graph, nodes: applyView(remaining, moved) };
    }, token);
    const stopped = session?.kind === "resize"
      ? changes.some(change => change.type === "dimensions" && change.resizing === false)
      : session?.kind === "drag" && changes.some(change => change.type === "position" && change.dragging === false);
    if (stopped && token) finishEdit(token);
  }, [currentGeneration, generation, isDocumentEditable, updateView, historyRef, beginEdit, execute, finishEdit, resolveBoxes, navigation.isPanning]);
  const onEdgesChange = useCallback((changes: EdgeChange[]) => {
    if (!currentGeneration() || navigation.isPanning()) return;
    const viewOnly = changes.filter(change => change.type === "select");
    const previewSession = previewRef.current;
    if (previewSession) {
      const next = { ...previewSession, graph: { ...previewSession.graph,
        edges: applyEdgeChanges(viewOnly, previewSession.graph.edges) } };
      previewRef.current = next; setPreview(next);
    } else if (isDocumentEditable() && changes.some(change => change.type !== "select")) {
      execute(graph => ({ ...graph, edges: applyEdgeChanges(changes, graph.edges) }));
    } else {
      updateView(graph => ({ ...graph, edges: applyEdgeChanges(viewOnly, graph.edges) }));
    }
  }, [currentGeneration, isDocumentEditable, updateView, execute, navigation.isPanning]);

  const installDiagram = useCallback((res: { diagram: DiagramDetail; effective_diagram_role_id?: DiagramRole | null }, personal = false) => {
    if (terminal.current || authorityRef.current.disposed) return;
    if (res.diagram.diagram_id !== diagramId) throw new Error("Unexpected diagram response");
    const accepted = decodeCheckpoint(res.diagram);
    if (checkpoint.current && compareRevision(accepted.revision, checkpoint.current.revision) < 0) return;
    const document = personal ? preserveCollaborationView(accepted.document, currentDocument()) : accepted.document;
    closeAuthority();
    snapshotRoot.current = document.snapshot;
    resetGraph({ nodes: document.snapshot.nodes, edges: document.snapshot.edges });
    setTitle(document.title); setDiagram(res.diagram);
    previewRef.current = null; setPreview(null); invalidatePreviewRequests();
    adopt(accepted);
    authorityRef.current.role = res.effective_diagram_role_id ?? authorityRef.current.role;
    setEffectiveRole(authorityRef.current.role);
    needsFitViewRef.current = !personal && !document.snapshot.viewport && document.snapshot.nodes.length > 0;
    if (!personal) {
      setViewport(document.snapshot.viewport ?? { x: 0, y: 0, zoom: 1 });
      setZoom(document.snapshot.viewport?.zoom ?? 1);
    }
    authorityRef.current.loaded = true; setError(null); setIsLoading(false);
  }, [diagramId, adopt, invalidatePreviewRequests, setViewport, resetGraph, closeAuthority, currentDocument, checkpoint]);

  const markUnavailable = useCallback(() => {
    if (terminal.current) return;
    // Commit an active uncontrolled text editor through its existing blur/session boundary.
    if (canvasRef.current?.contains(document.activeElement) && document.activeElement instanceof HTMLElement) document.activeElement.blur();
    setIsLoading(false);
    terminal.current = true; setUnavailable(true); invalidate(); closeAuthority(); invalidatePreviewRequests();
    setCanManageSharing(false); setPendingRequests(0); setShareOpen(false);
    if (checkpoint.current) setRecoveryCopies(copies => retainRecovery(copies, checkpoint.current!.revision, currentDocument()));
    pendingCheckpoint.current = null;
  }, [invalidate, closeAuthority, invalidatePreviewRequests, checkpoint, currentDocument]);

  const receiveCheckpoint = useCallback((wire: DiagramSyncCheckpoint, detail?: { diagram: DiagramDetail; effective_diagram_role_id?: DiagramRole | null }): boolean => {
    if (terminal.current || authorityRef.current.disposed) return false;
    const incoming = collaborationCheckpoint(diagramId!, wire);
    // A checkpoint hint cannot establish initial role/metadata authority by itself.
    if (!checkpoint.current && !detail) { pendingCheckpoint.current = higherCheckpoint(pendingCheckpoint.current, { ...wire, detail: detail ?? (pendingCheckpoint.current?.revision === wire.revision ? pendingCheckpoint.current.detail : undefined) }); return false; }
    const decision = reconcileCheckpoint({ accepted: checkpoint.current, incoming, working: currentDocument(),
      operation: operationRef.current !== null, preview: previewRef.current !== null,
      activeSession: historyRef.current.session !== null, submitted: submitted.current });
    if (decision === "older") return true;
    if (detail && !["buffer", "preview", "inconsistent"].includes(decision) && (!checkpoint.current || compareRevision(incoming.revision, checkpoint.current.revision) >= 0)) {
      const role = detail.effective_diagram_role_id ?? null;
      if (role !== authorityRef.current.role) {
        if (canvasRef.current?.contains(document.activeElement) && document.activeElement instanceof HTMLElement) document.activeElement.blur();
        closeAuthority(); authorityRef.current.role = role; setEffectiveRole(role); }
      setDiagram(detail.diagram);
    }
    if (decision === "duplicate") return true;
    if (["buffer", "preview", "conflict", "inconsistent"].includes(decision)) {
      pendingCheckpoint.current = higherCheckpoint(pendingCheckpoint.current, { ...wire, detail: detail ?? (pendingCheckpoint.current?.revision === wire.revision ? pendingCheckpoint.current.detail : undefined) });
      if (decision === "conflict") conflict();
      if (decision === "inconsistent") { conflict(); return false; }
      return decision === "preview" || decision === "conflict";
    }
    pendingCheckpoint.current = null;
    if (decision === "acknowledge") { adopt(incoming); return true; }
    const wasLoaded = authorityRef.current.loaded;
    const metadata = detail ?? { diagram: { ...wire, modified_date: "" }, effective_diagram_role_id: authorityRef.current.role };
    installDiagram(metadata, wasLoaded);
    if (wasLoaded) toast.info("Latest diagram loaded. Undo history cleared.");
    return true;
  }, [diagramId, checkpoint, operationRef, submitted, currentDocument, historyRef, adopt, conflict, installDiagram, closeAuthority]);

  // A count sets the pending requests, null (not a manager) clears them, and an absent field leaves them unchanged.
  const receiveSharing = useCallback((value: boolean | undefined, pending?: number | null) => {
    if (terminal.current || authorityRef.current.disposed) return;
    if (typeof value === "boolean") setCanManageSharing(value);
    if (typeof pending === "number") setPendingRequests(pending);
    else if (pending === null) setPendingRequests(0);
  }, []);

  const fetchAuthority = useCallback(async (refresh?: DiagramRefresh): Promise<boolean> => {
    const epoch = routeEpoch.current;
    if (terminal.current || authorityRef.current.disposed) return false;
    receiveSharing(refresh?.can_manage_sharing, refresh?.pending_access_request_count);
    if (refresh && checkpoint.current && compareRevision(refresh.revision, checkpoint.current.revision) < 0) return true;
    if (refresh && checkpoint.current && compareRevision(refresh.revision, checkpoint.current.revision) === 0 && !submitted.current && !pendingCheckpoint.current) {
      if (authorityRef.current.role !== refresh.effective_diagram_role_id) {
        if (canvasRef.current?.contains(document.activeElement) && document.activeElement instanceof HTMLElement) document.activeElement.blur();
        closeAuthority();
      }
      authorityRef.current.role = refresh.effective_diagram_role_id; setEffectiveRole(refresh.effective_diagram_role_id);
      return true;
    }
    try {
      const res = await api<DiagramDetailResponse>(`/api/diagrams/${diagramId}`);
      if (terminal.current || authorityRef.current.disposed || routeEpoch.current !== epoch) return false;
      receiveSharing(res.can_manage_sharing, res.pending_access_request_count);
      return receiveCheckpoint(res.diagram, res);
    } catch (error) {
      if (routeEpoch.current === epoch && error instanceof ApiError && [401, 403, 404].includes(error.status)) {
        if (error.status === 403 && !checkpoint.current) setNoAccess(true);
        markUnavailable();
      }
      throw error;
    }
  }, [diagramId, checkpoint, submitted, closeAuthority, receiveCheckpoint, markUnavailable, receiveSharing]);

  const sync = useDiagramSync(diagramId, {
    onBaseline: async (baseline: DiagramJoin) => receiveCheckpoint(baseline.diagram, baseline),
    onCheckpoint: wire => { try { receiveCheckpoint(wire); } catch { degradeRef.current(); } },
    onRefresh: fetchAuthority,
    onRefreshError: async () => { await fetchAuthority(); },
    onUnavailable: markUnavailable,
  });
  degradeRef.current = sync.degrade;
  useEffect(() => { if (unavailable) sync.retire(); }, [unavailable, sync.retire]);
  const presence = sync.presence.filter(member => member.connectionId !== sync.connectionId);
  useEffect(() => {
    if (operation || preview || !pendingCheckpoint.current || terminal.current) return;
    const pending = pendingCheckpoint.current;
    pendingCheckpoint.current = null;
    try { if (!receiveCheckpoint(pending, pending.detail)) sync.degrade(); } catch { sync.degrade(); }
  }, [operation, preview, receiveCheckpoint, sync.degrade]);

  const receiveRef = useRef(receiveCheckpoint); receiveRef.current = receiveCheckpoint;
  // Each effect instance owns cancellation, including development StrictMode replay.
  useEffect(() => {
    let cancelled = false;
    routeEpoch.current++;
    authorityRef.current.disposed = false; authorityRef.current.loaded = false;
    setIsLoading(true); setError(null);
    api<DiagramDetailResponse>(`/api/diagrams/${diagramId}`)
      .then((res) => {
        if (cancelled) return;
        rawLoad.current = res.diagram;
        receiveSharing(res.can_manage_sharing, res.pending_access_request_count);
        receiveRef.current(res.diagram, res);
      })
      .catch((error) => {
        if (cancelled) return;
        if (error instanceof ApiError && [401, 403, 404].includes(error.status)) {
          if (error.status === 403 && !checkpoint.current) setNoAccess(true);
          markUnavailable();
          return;
        }
        if (!checkpoint.current) setError("Failed to load diagram. The saved content could not be read safely.");
      })
      .finally(() => { if (!cancelled) setIsLoading(false); });
    return () => {
      cancelled = true; routeEpoch.current++;
      authorityRef.current.disposed = true; authorityRef.current.loaded = false;
      previewRef.current = null; invalidatePreviewRequests();
    };
  }, [diagramId, loadAttempt, invalidatePreviewRequests, markUnavailable, checkpoint, receiveSharing]);

  // ── Fit view after snapshot loads and nodes get measured ─
  useEffect(() => {
    if (previewRef.current || !needsFitViewRef.current || nodes.length === 0) return;
    // Check if nodes have been measured (have width/height from React Flow)
    const allMeasured = nodes.every((n) => n.measured?.width);
    if (allMeasured) {
      needsFitViewRef.current = false;
      rfInstance.fitView({ padding: 0.2 });
    }
  }, [nodes, rfInstance]);

  // ── Connection handler ─────────────────────────────────
  const connectionCallbacks = useMemo(() => {
    // This closure belongs to one route/history generation. A retained callback
    // cannot borrow a new gesture after Undo, preview, adoption or unmount.
    type Creation = { nodeId: string; handleId: string; route: number };
    const creation: { drag: Creation | null; click: Creation | null } = { drag: null, click: null };
    let reconnect: { origin: ReconnectOrigin; route: number } | null = null;
    const allowed = () => currentGeneration() && isDocumentEditable() && !navigation.isPanning();
    const start = (kind: "drag" | "click"): NonNullable<ReactFlowProps["onConnectStart"]> => (_event, params) => {
      // React Flow calls onReconnectStart before its nested onConnectStart.
      if (reconnect) return;
      creation[kind] = null;
      if (!allowed() || params.handleType !== "source" || !params.nodeId || !params.handleId ||
        !isConnectionPoint(historyRef.current.graph.nodes, params.nodeId, params.handleId)) return;
      creation[kind] = { nodeId: params.nodeId, handleId: params.handleId, route: routeEpoch.current };
    };
    const end = (kind: "drag" | "click") => () => { creation[kind] = null; };
    const callbacks: Pick<ReactFlowProps, "onConnectStart" | "onConnectEnd" | "onClickConnectStart" |
      "onClickConnectEnd" | "onConnect" | "onReconnectStart" | "onReconnect" | "onReconnectEnd" | "isValidConnection"> = {
      onConnectStart: start("drag"), onClickConnectStart: start("click"),
      onConnectEnd: end("drag"), onClickConnectEnd: end("click"),
      isValidConnection: proposed => allowed() &&
        (!reconnect || reconnect.route === routeEpoch.current) &&
        isValidConnection(historyRef.current.graph, proposed, reconnect?.origin),
      onConnect: proposed => {
        // Pointer start/end also run for each ordinary click. Keep the pending
        // click source independent until React Flow delivers its second click.
        const started = [creation.drag, creation.click].find(intent =>
          intent?.nodeId === proposed.source && intent.handleId === proposed.sourceHandle);
        creation.drag = null; creation.click = null;
        if (reconnect || !started || !allowed() || started.route !== routeEpoch.current ||
          proposed.source !== started.nodeId || proposed.sourceHandle !== started.handleId) return;
        execute(createConnection({ ...proposed, id: `edge_${crypto.randomUUID()}`, type: "custom",
          data: { lineStyle: "solid", arrowMode: "end", pathStyle: "smoothstep" } }));
      },
      onReconnectStart: (_event, edge, fixedSide) => {
        creation.drag = null; creation.click = null; reconnect = null;
        if (!allowed()) return;
        const current = historyRef.current.graph.edges.find(candidate => candidate.id === edge.id);
        // The installed callback reports the opposite (fixed) anchor's type.
        const side = fixedSide === "source" ? "target" : "source";
        if (!current || !canReconnect(current, side)) return;
        reconnect = { origin: { id: current.id, endpoints: connectionEndpoints(current), side }, route: routeEpoch.current };
      },
      onReconnect: (edge, proposed) => {
        const started = reconnect;
        if (!started || edge.id !== started.origin.id || !allowed() || started.route !== routeEpoch.current) return;
        // Consume the record once, retaining its presence until onReconnectEnd
        // so nested connection callbacks cannot turn this into a new edge.
        reconnect = { ...started, route: -1 };
        execute(reconnectConnection(started.origin, proposed));
      },
      onReconnectEnd: () => { reconnect = null; creation.drag = null; creation.click = null; },
    };
    return { callbacks, cancel: () => { creation.drag = null; creation.click = null; reconnect = null; } };
  }, [diagramId, currentGeneration, isDocumentEditable, historyRef, execute, navigation.isPanning]);

  cancelConnectionIntent.current = connectionCallbacks.cancel;

  const insertNode = useCallback((data: BoardNodeData, position: { x: number; y: number }) => {
    if (!isDocumentEditable()) return;
    const node = createBoardNode(`node_${crypto.randomUUID()}`, data, position);
    execute(graph => insertElements([node])({ ...graph, nodes: graph.nodes.map(existing => existing.selected ? { ...existing, selected: false } : existing) }));
  }, [isDocumentEditable, execute]);

  const handleAddNode = useCallback((data: BoardNodeData) => {
    if (!isDocumentEditable()) return;
    const bounds = canvasRef.current?.getBoundingClientRect();
    if (!bounds || bounds.width <= 0 || bounds.height <= 0) return;
    const center = rfInstance.screenToFlowPosition({
      x: bounds.left + bounds.width / 2,
      y: bounds.top + bounds.height / 2,
    });
    const size = NODE_DEFAULT_SIZES[data.type];
    insertNode(data, { x: center.x - size.width / 2, y: center.y - size.height / 2 });
  }, [isDocumentEditable, rfInstance, insertNode]);

  // ── Drag-and-drop from toolbar ─────────────────────────
  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = isDocumentEditable() ? "move" : "none";
  }, [isDocumentEditable]);

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      if (!isDocumentEditable()) return;
      const type = event.dataTransfer.getData("application/reactflow-type");
      const dataStr = event.dataTransfer.getData("application/reactflow-data");
      const data = parseNodeDrop(type, dataStr);
      if (!data) return;

      const position = rfInstance.screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      insertNode(data, position);
    },
    [isDocumentEditable, rfInstance, insertNode]
  );

  const onKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (event.defaultPrevented || event.nativeEvent.isComposing || event.altKey ||
      !isCanvasShortcutTarget(event.target, canvasRef.current)) return;
    navigation.updateModifiers(event);
    const action = canvasShortcut({ ...event, isComposing: event.nativeEvent.isComposing });
    if (!action) return;
    let taken = false;
    if (action === "select" || action === "pan") { navigation.changeMode(action); taken = true; }
    else if (action === "temporaryPan") { navigation.holdSpace(); taken = true; }
    else if (action === "cancel") { navigation.cancel(); selectVisible(false); taken = true; }
    else if (action === "selectAll") { selectVisible(true); taken = true; }
    else if (action === "shortcuts") { setShortcutReference("canvas"); taken = true; }
    else if (["zoomIn", "zoomOut", "actualSize", "fitBoard", "fitSelection"].includes(action)) taken = onNavigate(action as NavigationAction);
    else if (action === "copy" || action === "cut" || action === "paste" || action === "duplicate") {
      if (clipboard.available[action]) { void clipboard[action](); taken = true; }
    } else if (isDocumentEditable()) {
      if (action === "undo" && editability.canUndo) { undo(); taken = true; }
      else if (action === "redo" && editability.canRedo) { redo(); taken = true; }
      else if (action === "delete" && [...historyRef.current.graph.nodes, ...historyRef.current.graph.edges].some(item => item.selected && item.deletable !== false)) {
        execute(graph => removeElements([...expandGroupSelection(graph.nodes, graph.nodes.filter(node => node.selected).map(node => node.id))],
          graph.edges.filter(edge => edge.selected).map(edge => edge.id))(graph));
        taken = true;
      }
    }
    if (taken) { event.preventDefault(); event.stopPropagation(); }
  }, [clipboard, isDocumentEditable, execute, undo, redo, editability.canUndo, editability.canRedo, navigation, selectVisible, onNavigate]);

  const doSave = useCallback(async (source: "manual" | "autosave") => {
    if (!diagramId || !isDocumentEditable() || !sync.isConnected || operationRef.current || historyRef.current.session) return;
    invalidatePreviewRequests();
    const status = await saveCaptured(source);
    if (status === "unchanged" && source === "manual" && !authorityRef.current.disposed)
      toast.info("No changes to save", { description: "Diagram is already up to date." });
  }, [diagramId, isDocumentEditable, operationRef, invalidatePreviewRequests, saveCaptured, sync.isConnected, historyRef]);
  const handleSave = useCallback(() => doSave("manual"), [doSave]);

  useEffect(() => {
    if (!dirty || failure || operation || isPreviewing || !sync.isConnected || history.state.session) return;
    const timer = setTimeout(() => { void doSave("autosave"); }, 750);
    return () => clearTimeout(timer);
  }, [workingDocument, dirty, failure, operation, isPreviewing, sync.isConnected, history.state.session, doSave]);

  const downloadLocal = () => {
    try { downloadRecovery(diagramId!, recoveryCopy(diagramId!, checkpoint.current?.revision ?? null, workingDocument)); }
    catch { toast.error("Could not download the local copy. Your edits are still here."); }
  };

  async function reloadLatest() {
    const authority = authorityRef.current;
    if (terminal.current || !authority.loaded || authority.disposed || !getBoardPermissions(authority.role).canRestore || operationRef.current || !window.confirm("Reload the latest diagram and replace your local edits? Download a local copy first if you want to keep them.")) return;
    const op = begin("reload");
    if (!op) return;
    const epoch = routeEpoch.current;
    invalidatePreviewRequests();
    try {
      const res = await api<DiagramDetailResponse>(`/api/diagrams/${diagramId}`);
      if (op.current() && routeEpoch.current === epoch) {
        installDiagram(res, true);
        receiveSharing(res.can_manage_sharing, res.pending_access_request_count);
        pendingCheckpoint.current = null;
        sync.refresh();
        toast.info("Latest diagram loaded. Undo history cleared.");
      }
    } catch (error) {
      if (op.current() && routeEpoch.current === epoch) {
        if (error instanceof ApiError && [401, 403, 404].includes(error.status)) markUnavailable();
        fail(error, true); toast.error("Failed to reload diagram. Your local edits are retained.");
      }
    } finally { op.end(); }
  }

  // ── Version preview / restore ──────────────────────────
  function handlePreviewVersion(snapshot: unknown): boolean {
    if (authorityRef.current.disposed || !authorityRef.current.loaded || operationRef.current) return false;
    try {
      const next = beginHistoryPreview(snapshot, previewRef.current, rfInstance.getViewport());
      // Close command authority before React mounts the historical graph (equal
      // node ids can retain existing editors until the next render).
      closeAuthority();
      previewRef.current = next;
      needsFitViewRef.current = false;
      setPreview(next);
      rfInstance.setViewport(next.viewport);
      setZoom(next.viewport.zoom);
      return true;
    } catch {
      toast.error("Failed to load version snapshot");
      return false;
    }
  }

  async function handleExitPreview() {
    invalidatePreviewRequests();
    if (terminal.current || operationRef.current || !previewRef.current || !checkpoint.current) return;
    const op = begin("reload");
    if (!op) return;
    const epoch = routeEpoch.current, session = previewRef.current;
    try {
      const res = await api<DiagramDetailResponse>(`/api/diagrams/${diagramId}`);
      if (!op.current() || routeEpoch.current !== epoch || terminal.current) return;
      const accepted = collaborationCheckpoint(diagramId!, res.diagram);
      if (compareRevision(accepted.revision, checkpoint.current.revision) < 0) throw new Error("Stale preview exit response");
      if (compareRevision(accepted.revision, checkpoint.current.revision) === 0 && canonicalDocument(accepted.document) !== canonicalDocument(checkpoint.current.document))
        throw new Error("Inconsistent preview exit checkpoint");
      if (compareRevision(accepted.revision, checkpoint.current.revision) > 0) {
        const working = currentDocument(), baseRevision = checkpoint.current.revision;
        if (canonicalDocument(working) !== canonicalDocument(checkpoint.current.document))
          setRecoveryCopies(copies => retainRecovery(copies, baseRevision, working));
        installDiagram(res, true);
        toast.info("Latest diagram loaded. Undo history cleared.");
      }
      const exit = exitHistoryPreview(session);
      previewRef.current = null; setPreview(null);
      rfInstance.setViewport(exit.viewport); setZoom(exit.viewport.zoom);
      pendingCheckpoint.current = null;
      authorityRef.current.role = res.effective_diagram_role_id ?? null; setEffectiveRole(authorityRef.current.role);
      receiveSharing(res.can_manage_sharing, res.pending_access_request_count);
    } catch (error) {
      if (op.current() && routeEpoch.current === epoch) {
        if (error instanceof ApiError && [401, 403, 404].includes(error.status)) markUnavailable();
        else toast.error("Could not refresh the current diagram. Preview and local work are retained; try Exit Preview again.");
      }
    } finally { op.end(); }
  }

  async function handleRestore(versionId: string): Promise<boolean> {
    const authority = authorityRef.current;
    if (!diagramId || terminal.current || !sync.isConnected || authority.disposed || !authority.loaded ||
      !getBoardPermissions(authority.role).canRestore || operationRef.current) return false;
    if (!checkpoint.current) return false;
    const op = begin("restore");
    if (!op) return false;
    const epoch = routeEpoch.current;
    invalidatePreviewRequests();
    try {
      const res = await api<DiagramSaveResult & { effective_diagram_role_id?: DiagramRole | null }>(`/api/diagrams/${diagramId}/restore/${versionId}`, {
        method: "POST", body: JSON.stringify({ expected_revision: checkpoint.current.revision }),
      });
      if (!op.current() || routeEpoch.current !== epoch) return false;
      if (!res.success) throw new Error("Invalid restore acknowledgement");
      installDiagram(res);
      toast.success("Version restored. Undo history cleared.");
      return true;
    } catch (error) {
      if (op.current() && routeEpoch.current === epoch) {
        fail(error, true); toast.error("Failed to restore version");
      }
      return false;
    } finally { op.end(); }
  }

  // ── Render ─────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-sm text-muted-foreground">Loading diagram...</p>
      </div>
    );
  }

  if (error) {
    return <div role="alert" className="flex h-full flex-col items-center justify-center gap-3 text-sm">
      <p>{error}</p>
      <Button onClick={() => setLoadAttempt(n => n + 1)}>Retry loading</Button>
      {rawLoad.current !== null && <Button variant="outline" onClick={() => {
        try { downloadRecovery(diagramId!, rawLoad.current); } catch { toast.error("Could not download saved content"); }
      }}>Download saved content</Button>}
      <Link to="/">Back to dashboard</Link>
    </div>;
  }

  return (
    <CanvasEditabilityProvider value={editability}>
    <div className="flex h-full min-h-0 min-w-0 flex-col">
      {/* Header toolbar */}
      <div className="flex min-w-0 flex-wrap items-center gap-2 border-b px-3 py-2">
        <Input
          value={title}
          aria-label="Diagram title"
          readOnly={!canEditDocument}
          onChange={(e) => {
            const value = e.target.value;
            runDocumentMutation(() => setTitle((current) => isDocumentEditable() ? value : current));
          }}
          className="min-w-24 max-w-xs flex-1 border-none bg-transparent text-sm font-medium shadow-none"
          placeholder="Untitled diagram"
        />
        <div className="flex flex-wrap items-center gap-1">
          <Button
            size="sm"
            onClick={handleSave}
            disabled={!canEditDocument || !sync.isConnected || operation !== null || (failure !== null && failure.kind !== "failed")}
            className={cn(
              "min-w-[72px] transition-all duration-300",
              saveState === "saved" && "bg-green-600 text-white hover:bg-green-600"
            )}
          >
            {saveState === "saving" ? (
              "Saving..."
            ) : saveState === "saved" ? (
              <span className="flex flex-wrap items-center gap-1">
                <Check className="h-3.5 w-3.5" />
                Saved
              </span>
            ) : (
              "Save"
            )}
          </Button>
          {showSharing && (
            <div className="relative">
              <Button size="sm" variant="outline" onClick={() => setShareOpen(true)}>
                Share
              </Button>
              {/* A sibling of the button, so the button's accessible name stays "Share". */}
              {pendingRequests > 0 && (
                <span
                  role="status"
                  aria-label={`${pendingRequests} pending access request${pendingRequests === 1 ? "" : "s"}`}
                  className="pointer-events-none absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-medium leading-none text-primary-foreground"
                >
                  {pendingRequests}
                </span>
              )}
            </div>
          )}
          <Button
            size="sm"
            variant={commentsOpen ? "secondary" : "outline"}
            onClick={() => {
              setCommentsOpen((v) => !v);
              if (!commentsOpen) { invalidatePreviewRequests(); setHistoryOpen(false); }
            }}
          >
            Comments
          </Button>
          <Button
            size="sm"
            variant={historyOpen ? "secondary" : "outline"}
            onClick={() => {
              invalidatePreviewRequests();
              setHistoryOpen((v) => !v);
              if (!historyOpen) setCommentsOpen(false);
            }}
          >
            History
          </Button>
        </div>

        {/* Presence — only show other collaborators */}
        <div className="ml-auto flex min-w-0 max-w-full items-center gap-2">
          {presence.length > 0 && (
            <div className="flex flex-wrap items-center gap-1">
              {presence.map((p) => (
                <Badge key={p.connectionId} variant="secondary" className="max-w-36 truncate text-xs" title={p.displayName || p.userId}>
                  {p.displayName || p.userId}
                </Badge>
              ))}
            </div>
          )}
          <CollaborationStatus status={sync.status} onRetry={sync.retry} unavailable={unavailable} />
        </div>

        {dirty && <span className="text-xs text-muted-foreground">Unsaved changes</span>}
      </div>

      {unavailable && (
        <div role="alert" className="border-b bg-muted px-4 py-3 text-sm">
          This diagram is unavailable. Your local content is retained; download it before leaving.
          {noAccess && diagramId && (
            <>
              {" "}
              <Button asChild variant="link" size="sm" className="h-auto p-0">
                <Link to={`/d/${diagramId}/access-request`}>Request access</Link>
              </Button>
            </>
          )}
        </div>
      )}
      {(failure || recoveryCopies.length > 0) && <SaveRecoveryPanel failure={failure} disabled={!canRestore || operation !== null} retryDisabled={!canEditDocument || !sync.isConnected}
        onRetry={handleSave} onDownload={downloadLocal} onReload={reloadLatest}
        copies={recoveryCopies} onDownloadCopy={copy => downloadRecovery(diagramId!, recoveryCopy(diagramId!, copy.baseRevision, copy.document))}
        onDiscardCopy={id => setRecoveryCopies(copies => copies.filter(copy => copy.id !== id))} />}

      {/* Preview banner */}
      {isPreviewing && (
        <div className="flex items-center justify-between border-b border-yellow-500/50 bg-yellow-500/10 px-4 py-2">
          <p className="text-sm font-medium text-yellow-700 dark:text-yellow-400">
            Previewing an older version — edits are disabled
          </p>
          <Button
            size="sm"
            variant="outline"
            className="border-yellow-500/50 text-yellow-700 hover:bg-yellow-500/10 dark:text-yellow-400"
            disabled={operation !== null || unavailable}
            onClick={handleExitPreview}
          >
            Exit Preview
          </Button>
        </div>
      )}

      {/* Canvas + panels */}
      <div className="flex min-h-0 min-w-0 flex-1 overflow-hidden">
        <div
          ref={canvasRef}
          className="board-canvas relative min-h-0 min-w-0 flex-1"
          data-canvas-mode={interaction.panning ? "pan" : "select"}
          role="region" aria-label="Diagram canvas"
          onKeyDownCapture={onKeyDown}
          onPointerDownCapture={event => {
            navigation.updateModifiers(event);
            if (event.target instanceof Element && event.target.classList.contains("react-flow__pane")) returnCanvasFocus();
          }}
          onPointerMove={event => {
            const bounds = canvasRef.current?.getBoundingClientRect();
            if (!bounds || event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) return;
            const point = rfInstance.screenToFlowPosition({ x: event.clientX, y: event.clientY });
            sync.sendCursor(point.x, point.y);
          }}
          onMouseDownCapture={navigation.updateModifiers}
          onDoubleClickCapture={event => {
            if (navigation.isPanning() && isCanvasShortcutTarget(event.target, canvasRef.current)) { event.preventDefault(); event.stopPropagation(); }
          }}
          tabIndex={0}
        >
          <ReactFlow
            nodes={displayedNodes}
            edges={displayedEdges}
            defaultViewport={snapshotRoot.current.viewport}
            elevateNodesOnSelect={false}
            nodesDraggable={interaction.nodesDraggable}
            nodesConnectable={interaction.nodesConnectable}
            edgesReconnectable={interaction.edgesReconnectable}
            elementsSelectable={interaction.elementsSelectable}
            selectionOnDrag={interaction.selectionOnDrag}
            selectionMode={SelectionMode.Full}
            panOnDrag={interaction.panOnDrag}
            panActivationKeyCode={null}
            selectionKeyCode={null}
            multiSelectionKeyCode={null}
            zoomActivationKeyCode={null}
            minZoom={MIN_CANVAS_ZOOM}
            maxZoom={MAX_CANVAS_ZOOM}
            panOnScroll
            zoomOnScroll={false}
            zoomOnPinch
            zoomOnDoubleClick={false}
            onBeforeDelete={async ({ nodes, edges }) => {
              if (!currentGeneration() || !isDocumentEditable()) return false;
              execute(graph => removeElements([...expandGroupSelection(graph.nodes, nodes.map(node => node.id))], edges.map(edge => edge.id))(graph));
              return false;
            }}
            onNodeDragStart={startDrag}
            onNodeDragStop={endDrag}
            onSelectionDragStart={startDrag}
            onSelectionDragStop={endDrag}
            onSelectionStart={() => { if (currentGeneration()) selectionBoxGeneration.current = generation; }}
            onSelectionEnd={() => { if (currentGeneration()) selectionBoxGeneration.current = null; }}
            onMove={(_, viewport) => {
              setZoom(viewport.zoom);
              pasteCascade.current = null;
              const session = previewRef.current;
              if (session) {
                // Navigation changes no rendered graph; avoid re-adopting every
                // node and edge on each pan/zoom frame.
                previewRef.current = { ...session, viewport: { ...viewport } };
              }
            }}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            connectionMode={ConnectionMode.Loose}
            {...connectionCallbacks.callbacks}
            onDragOver={onDragOver}
            onDrop={onDrop}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            defaultEdgeOptions={{ type: "custom" }}
            deleteKeyCode={null}
            className="bg-background"
          >
            <CollaboratorCursors cursors={sync.cursors} presence={sync.presence} connectionId={sync.connectionId} />
            <Background />
            <MiniMap
              pannable
              zoomable
              position="top-right"
              className="board-minimap !bg-background/80"
            />
            <Panel position="top-left" className="!m-0 !p-0">
              <ArrowMarkerDefs />
            </Panel>
          </ReactFlow>
          <CanvasNavigationControls mode={navigation.mode} temporaryPan={navigation.temporaryPan} zoom={zoom}
            available={navigationAvailability(measuredGraph(), zoom)}
            onModeChange={navigation.changeMode} onNavigate={onNavigate} reference={shortcutReference}
            onReferenceChange={setShortcutReference} onReturnFocus={returnCanvasFocus} />
          <DiagramToolbar nodes={visibleGraph.nodes} onAddNode={handleAddNode} clipboard={clipboard}
            organization={{ ...organization, onAction: onOrganize, onClose: () => canvasRef.current?.focus({ preventScroll: true }) }}
            onClipboardClose={() => canvasRef.current?.focus({ preventScroll: true })} />
        </div>

        {diagramId && commentsOpen && (
          <CommentsPanel
            diagramId={diagramId}
            open={commentsOpen}
            onClose={() => setCommentsOpen(false)}
          />
        )}

        {diagramId && historyOpen && (
          <VersionHistoryPanel
            diagramId={diagramId}
            open={historyOpen}
            onClose={() => { invalidatePreviewRequests(); setHistoryOpen(false); }}
            canRestore={canRestore}
            historyBusy={operation !== null}
            getPreviewRequestKey={getPreviewRequestKey}
            onRestore={handleRestore}
            onPreview={handlePreviewVersion}
            onExitPreview={handleExitPreview}
            isPreviewing={isPreviewing}
          />
        )}
      </div>

      {diagramId && showSharing && (
        <ShareDialog
          diagramId={diagramId}
          open={shareOpen}
          onOpenChange={setShareOpen}
          onPendingCountChange={setPendingRequests}
        />
      )}
    </div>
    </CanvasEditabilityProvider>
  );
}

// Outer wrapper provides ReactFlowProvider context
export function DiagramPage() {
  const { diagramId } = useParams();
  return (
    <ReactFlowProvider key={diagramId}>
      <DiagramCanvas key={diagramId} />
    </ReactFlowProvider>
  );
}
